/**
 * Access catalog (web) — must stay identical to `apps/api/src/domain/access.ts`.
 * Nav, route gates, mutation buttons, and the admin Roles & access reference all read from here.
 * Change ROLE_AREAS / ROLE_ACTIONS to reshape who sees what; keep the API file in lockstep.
 */

import type { Role } from '../api';

export const ROLES: readonly Role[] = ['admin', 'legal', 'fbc', 'finance', 'cdto', 'coo', 'gtm'];

export const AREAS = [
  'dashboard',
  'estate',
  'products',
  'links',
  'activation',
  'responses',
  'reports',
  'ops',
  'audit',
  'users',
  'settings',
  'about',
  'vision',
] as const;
export type Area = (typeof AREAS)[number];

export const ACTIONS = [
  'products.manage',
  'content.edit',
  'variables.edit',
  'links.manage',
  'agreements.manage',
  'annotations.write',
  'grants.manage',
  'settings.manage',
  'emulate',
  'digest.send',
] as const;
export type Action = (typeof ACTIONS)[number];

export const AREA_META: Record<Area, { label: string; description: string }> = {
  dashboard: { label: 'Dashboard', description: 'Adoption readiness overview for your scope' },
  estate: { label: 'Estate', description: 'Franchisees, BMUs, and restaurant counts in your scope' },
  products: { label: 'Products', description: 'Catalog, pricing, availability, and terms' },
  links: { label: 'Adoption Links', description: 'Market adoption URLs (view or manage)' },
  activation: { label: 'Activation', description: 'Market agreement coverage and chase board' },
  responses: { label: 'Responses', description: 'Franchisee accept / decline activity in your scope' },
  reports: { label: 'Reports', description: 'Billing readiness and export' },
  ops: { label: 'Operations', description: 'Sync health and operational runs (whole system)' },
  audit: { label: 'Audit', description: 'Compliance event trail (whole system)' },
  users: { label: 'Users & Access', description: 'Invite, re-scope, and revoke grants' },
  settings: { label: 'Settings', description: 'Portal settings, variables, email previews' },
  about: { label: 'About', description: 'Product value and how the portal works' },
  vision: { label: 'Vision', description: 'Stakeholder vision deck' },
};

export const ACTION_META: Record<Action, { label: string; description: string }> = {
  'products.manage': { label: 'Manage products', description: 'Create, edit, price, and enable products' },
  'content.edit': { label: 'Edit terms', description: 'Draft and publish product terms content' },
  'variables.edit': { label: 'Edit variables', description: 'Terms merge-field defaults and overrides' },
  'links.manage': { label: 'Manage links', description: 'Create, pause, and regenerate adoption links' },
  'agreements.manage': { label: 'Manage agreements', description: 'Record or upload market agreements' },
  'annotations.write': { label: 'Write annotations', description: 'Activation notes on markets in scope' },
  'grants.manage': { label: 'Manage grants', description: 'Invite and revoke portal access' },
  'settings.manage': { label: 'Manage settings', description: 'Change portal-wide settings' },
  emulate: { label: 'Emulate user', description: 'View the portal as another YUM_ID (global admin)' },
  'digest.send': { label: 'Send digest', description: 'Trigger the activation digest email' },
};

const OVERSIGHT_SUMMARY =
  'Scoped oversight: dashboard, estate, adoption links, and responses for their markets.';

export const ROLE_META: Record<Role, { label: string; badge: string; summary: string }> = {
  admin: {
    label: 'Admin',
    badge: 'Admin',
    summary: 'Full portal: catalog, rollout, system surfaces, and user access. Data still filtered by grant scope.',
  },
  legal: {
    label: 'Legal',
    badge: 'Legal',
    summary: `${OVERSIGHT_SUMMARY} (Catalog and activation can be re-enabled in the access catalog when needed.)`,
  },
  fbc: {
    label: 'Franchise Business Coach',
    badge: 'FBC',
    summary: OVERSIGHT_SUMMARY,
  },
  finance: {
    label: 'Finance',
    badge: 'Finance',
    summary: OVERSIGHT_SUMMARY,
  },
  cdto: {
    label: 'Chief Digital / Technology Officer',
    badge: 'CDTO',
    summary: OVERSIGHT_SUMMARY,
  },
  coo: {
    label: 'Chief Operating Officer',
    badge: 'COO',
    summary: OVERSIGHT_SUMMARY,
  },
  gtm: {
    label: 'Go-to-Market',
    badge: 'GTM',
    summary: OVERSIGHT_SUMMARY,
  },
};

const OVERSIGHT_AREAS: readonly Area[] = ['dashboard', 'estate', 'links', 'responses'];

export const ROLE_AREAS: Record<Role, readonly Area[]> = {
  admin: AREAS,
  legal: OVERSIGHT_AREAS,
  fbc: OVERSIGHT_AREAS,
  finance: OVERSIGHT_AREAS,
  cdto: OVERSIGHT_AREAS,
  coo: OVERSIGHT_AREAS,
  gtm: OVERSIGHT_AREAS,
};

export const ROLE_ACTIONS: Record<Role, readonly Action[]> = {
  admin: ACTIONS,
  legal: [],
  fbc: [],
  finance: [],
  cdto: [],
  coo: [],
  gtm: [],
};

export function hasArea(role: Role | undefined, area: Area): boolean {
  if (!role) return false;
  return ROLE_AREAS[role].includes(area);
}

export function hasAction(role: Role | undefined, action: Action): boolean {
  if (!role) return false;
  return ROLE_ACTIONS[role].includes(action);
}

/** Route path → area (used by RequireArea and nav). */
export const AREA_PATH: Record<Area, string> = {
  dashboard: '/admin',
  estate: '/admin/estate',
  products: '/admin/products',
  links: '/admin/links',
  activation: '/admin/activation',
  responses: '/admin/responses',
  reports: '/admin/reports',
  ops: '/admin/ops',
  audit: '/admin/audit',
  users: '/admin/users',
  settings: '/admin/settings',
  about: '/admin/about',
  vision: '/admin/vision',
};

/** Resolve which area a pathname belongs to (longest prefix wins). */
export function areaForPath(pathname: string): Area | undefined {
  const entries = (Object.entries(AREA_PATH) as [Area, string][])
    .filter(([, path]) => (path === '/admin' ? pathname === '/admin' || pathname === '/admin/' : pathname === path || pathname.startsWith(`${path}/`)))
    .sort((a, b) => b[1].length - a[1].length);
  return entries[0]?.[0];
}
